import { type ClusterPeriod, type Period, type PeriodTable } from '../../common/types';

import { type Direction, type Indexes, type Item } from './types';

/**
 * @param items - List of items
 * @param epoch - Target epoch
 * @returns index - Index of nearest item to the given epoch (prefers future side of epoch)
 * @returns null - If item not found
 */
const getClosestInList = (items: Item[], epoch: number): number | null => {
	if (items.length === 0) {
		return null;
	}

	if (epoch <= items[0].epoch) {
		return 0;
	}

	if (epoch >= items[items.length - 1].epoch) {
		return items.length - 1;
	}

	let left = 0;
	let right = items.length - 1;

	while (left <= right) {
		let mid = Math.floor((left + right) / 2);

		if (epoch < items[mid].epoch) {
			right = mid - 1;
		} else if (epoch > items[mid].epoch) {
			left = mid + 1;
		} else {
			return mid;
		}
	}

	return items[left].epoch - epoch < epoch - items[right].epoch ? left : right;
};

/**
 * @param table - Table of items
 * @param epoch - Target epoch
 * @returns [index, index] - Index of nearest item to the given epoch (prefers future side of epoch)
 * @returns null - If item not found
 */
const getClosestInTable = (table: Item[][], epoch: number): Indexes | null => {
	let size = table.length;

	if (size === 0) {
		return null;
	}

	let column: number | null = null;
	let row: number | null = null;

	for (let i = 0; i < size; i++) {
		column = getClosestInList(table[i], epoch);

		if (column !== null) {
			row = i;
			break;
		}
	}

	if (row === null || column === null) {
		return null;
	}

	return [row, column];
};

/**
 * @param table - Table of items
 * @returns move - Function to move focus
 */
const move =
	(table: Item[][]) =>
	/**
	 * @param current - Current index
	 * @param direction - Direction to move
	 * @returns [index, index] - Index of nearest item to the given epoch
	 */
	(current: Indexes | null, direction: Direction): Indexes | null => {
		if (
			table.every(function (row) {
				return !row.length;
			}) ||
			!current
		) {
			return null;
		}

		const size = table.length;
		const [row, column] = current;

		switch (direction) {
			case 'up': {
				if (row === 0) {
					return [row, column];
				}

				let r = row - 1;
				let c = getClosestInList(table[r], table[row][column].epoch);

				while (c === null && r > 0) {
					r--;
					c = getClosestInList(table[r], table[row][column].epoch);
				}

				if (c === null) {
					return [row, column];
				}

				return [r, c];
			}
			case 'down': {
				if (row === size - 1) {
					return [row, column];
				}

				let r = row + 1;
				let c = getClosestInList(table[r], table[row][column].epoch);

				while (c === null && r < size - 1) {
					r++;
					c = getClosestInList(table[r], table[row][column].epoch);
				}

				if (c === null) {
					return [row, column];
				}

				return [r, c];
			}
			case 'left': {
				return column === 0 ? [row, column] : [row, column - 1];
			}
			case 'right': {
				return column === table[row].length - 1 ? [row, column] : [row, column + 1];
			}
			default: {
				return [row, column];
			}
		}
	};

/**
 * @param table - Table of items
 * @param item - Target item
 * @returns [index, index] - Index of item in the table
 * @returns null - If item not found
 */
const findItemIndexInTable = (table: Item[][], id: string): Indexes | null => {
	const size = table.length;

	for (let i = 0; i < size; i++) {
		const index = table[i].findIndex((x) => x.id === id);

		if (index !== -1) {
			return [i, index];
		}
	}

	return null;
};

/**
 * @param table - Table of items
 * @param item - Target item
 * @returns Item - Item from the table
 * @returns null - If item not found
 */
const getItem = (table: Item[][], indexes: Indexes | null) => {
	if (indexes === null) {
		return null;
	}

	const [row, column] = indexes;

	return table?.[row]?.[column] ?? null;
};

const fromClusterPeriodsToItemTable = (clusterPeriods: ClusterPeriod[]): Item[][] => [
	clusterPeriods.flatMap((clusterPeriod) =>
		clusterPeriod.overlaps.map((overlap) => ({
			id: overlap.id,
			epoch: overlap.startEpoch + (overlap.endEpoch - overlap.startEpoch) / 2,
		})),
	),
];

const fromRotationsToItemTable = (rotations: PeriodTable<Period>[]): Item[][] =>
	rotations.map((rotation) =>
		rotation.periods.map((period) => ({
			id: period.id,
			epoch: period.startEpoch + (period.endEpoch - period.startEpoch) / 2,
		})),
	);

/*
  Week = {
    rotations: {
      id: string,
      periods: {
      id,
      startEpoch,
      endEpoch
      }[]
    }[]
  }[]

  to 
  
  Item = {
    id: string,
    epoch: number
  }[][]
 */

const fromWeeksToItemTable = (weeks: { rotations: PeriodTable<Period>[] }[]): Item[][] => {
	const table: Record<string, Item[]> = {};

	weeks.forEach((week) => {
		week.rotations.forEach((rotation) => {
			if (!table[rotation.id]) {
				table[rotation.id] = [] as Item[];
			}

			table[rotation.id].push(
				...rotation.periods.map((period) => ({
					id: period.id,
					epoch: period.startEpoch + (period.endEpoch - period.startEpoch) / 2,
				})),
			);
		});
	});

	return Object.values(table);
};

export {
	findItemIndexInTable,
	fromClusterPeriodsToItemTable,
	fromRotationsToItemTable,
	fromWeeksToItemTable,
	getClosestInList,
	getClosestInTable,
	getItem,
	move,
};
